import {
  DehydratedState,
  QueryClient,
  dehydrate,
  hydrate,
} from "@tanstack/react-query";
import { CACHE_KEY_PREFIX, type CacheStorage } from "./cache-types";

/**
 * Persisted query cache ("stale-while-revalidate across reloads").
 *
 * What the user saw last is written to storage and put back on the next
 * visit, so screens render at once and refresh in the background (every
 * restored entry is older than its stale time, so mounting a screen refetches
 * it). The cache is per user and organization, is dropped after 24 hours, and
 * is wiped on sign-out and on an org switch (see QueryCachePersistence).
 */

/** Bump to discard every persisted cache after an incompatible data change. */
export const CACHE_VERSION = 2;
/** A persisted cache, and any single entry in it, older than this is ignored. */
export const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Lists (infinite queries) keep only their first pages in storage. */
export const PERSISTED_PAGES = 3;
/** Writes are batched; this is the longest a change waits to be stored. */
export const PERSIST_THROTTLE_MS = 1000;

export type CacheIdentity = Readonly<{ userId: string; orgId: string }>;

type PersistedCache = Readonly<{
  version: number;
  savedAt: number;
  userId: string;
  orgId: string;
  state: DehydratedState;
}>;

export const cacheKeyFor = ({ userId, orgId }: CacheIdentity) =>
  `${CACHE_KEY_PREFIX}${userId}:${orgId}`;

/** Marks an item that exists only on this device until the server confirms it. */
export const OPTIMISTIC_FLAG = "__optimistic";

const stripOptimistic = (page: unknown): unknown => {
  if (!page || typeof page !== "object" || Array.isArray(page)) return page;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(page)) {
    const items = (value as { items?: unknown })?.items;
    out[key] = Array.isArray(items)
      ? {
          ...(value as object),
          items: items.filter(
            (item) => !(item && (item as Record<string, unknown>)[OPTIMISTIC_FLAG]),
          ),
        }
      : value;
  }
  return out;
};

/** Infinite-query data trimmed to its first PERSISTED_PAGES pages. */
export const trimPages = (data: unknown): unknown => {
  const d = data as { pages?: unknown[]; pageParams?: unknown[] } | null;
  if (!d || !Array.isArray(d.pages) || !Array.isArray(d.pageParams)) return data;
  return {
    ...d,
    pages: d.pages.slice(0, PERSISTED_PAGES).map(stripOptimistic),
    pageParams: d.pageParams.slice(0, PERSISTED_PAGES),
  };
};

/** The cache as stored: successful queries only, lists trimmed. */
export const snapshotCache = (client: QueryClient): DehydratedState => {
  const state = dehydrate(client, {
    shouldDehydrateQuery: (query) =>
      query.state.status === "success" &&
      query.state.data !== undefined &&
      query.meta?.persist !== false,
    shouldDehydrateMutation: () => false,
  });
  return {
    ...state,
    queries: state.queries.map((q) => ({
      ...q,
      state: { ...q.state, data: trimPages(q.state.data) },
    })),
  };
};

export const writeCache = async (
  storage: CacheStorage,
  client: QueryClient,
  identity: CacheIdentity,
  now = Date.now(),
) => {
  const payload: PersistedCache = {
    version: CACHE_VERSION,
    savedAt: now,
    userId: identity.userId,
    orgId: identity.orgId,
    state: snapshotCache(client),
  };
  await storage.set(cacheKeyFor(identity), JSON.stringify(payload));
};

/**
 * Puts the stored cache for `identity` back into the client. Returns how many
 * entries were restored; 0 when there is nothing usable (none, another
 * version, another identity, or older than CACHE_MAX_AGE_MS).
 */
export const restoreCache = async (
  storage: CacheStorage,
  client: QueryClient,
  identity: CacheIdentity,
  now = Date.now(),
): Promise<number> => {
  const key = cacheKeyFor(identity);
  let parsed: PersistedCache | null = null;
  try {
    const raw = await storage.get(key);
    parsed = raw ? (JSON.parse(raw) as PersistedCache) : null;
  } catch {
    parsed = null;
  }
  if (
    !parsed ||
    parsed.version !== CACHE_VERSION ||
    parsed.userId !== identity.userId ||
    parsed.orgId !== identity.orgId ||
    !(now - parsed.savedAt < CACHE_MAX_AGE_MS)
  ) {
    if (parsed) await storage.remove(key).catch(() => {});
    return 0;
  }
  const queries = (parsed.state.queries ?? []).filter(
    (q) => now - (q.state.dataUpdatedAt ?? 0) < CACHE_MAX_AGE_MS,
  );
  hydrate(client, { mutations: [], queries });
  return queries.length;
};

/**
 * Keeps storage in step with the cache: a throttled write after changes, and
 * an immediate one when the page is hidden (tab switch, close, reload).
 * Returns a stop function; its promise settles once any write already under
 * way has finished, so a wipe that follows cannot be undone by a late write.
 */
export const subscribeCache = (
  storage: CacheStorage,
  client: QueryClient,
  identity: CacheIdentity,
  throttleMs = PERSIST_THROTTLE_MS,
) => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let pending: Promise<void> = Promise.resolve();
  const flush = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (stopped) return;
    pending = pending
      .then(() => (stopped ? undefined : writeCache(storage, client, identity)))
      .catch(() => {});
  };
  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" && event.type !== "removed") return;
    if (!timer) timer = setTimeout(flush, throttleMs);
  });
  const onHide = () => {
    if (typeof document === "undefined" || document.visibilityState === "hidden") flush();
  };
  const hasWindow = typeof window !== "undefined" && !!window.addEventListener;
  if (hasWindow) {
    window.addEventListener("pagehide", onHide);
    document.addEventListener?.("visibilitychange", onHide);
  }
  return (): Promise<void> => {
    stopped = true;
    if (timer) clearTimeout(timer);
    timer = null;
    unsubscribe();
    if (hasWindow) {
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener?.("visibilitychange", onHide);
    }
    return pending;
  };
};
